/**
 * One uploaded file, as the Studio's media layer and its persistence record need it.
 *
 * WHY THIS FILE EXISTS. P5a's `ClipSource.uploadedFileId` is documented as coming from
 * "`FileMetadata.getFileIdFromSafeName(location)`" (`media/types.ts:36`, `state/persistence.ts:85`,
 * STUDIO_PLAN_P5A.md:4475). That function DOES exist — `types/types.ts:333-345`, a namespace
 * merged onto the `FileMetadata` interface — and P5c's D4 is wrong to say it does not (measured
 * 2026-09-17; the plan's own grep missed it). It is still the wrong thing to call, for two
 * reasons that are facts about the function rather than about its existence:
 *
 *   1. IT THROWS. `getFileIdFromSafeName` raises `Error('Failed to get uploaded file id …')` for
 *      a name it cannot read. Every caller here has a name it may well not be able to read — a
 *      record restored from another build, a rendition path, a user file — and "the clip has no
 *      addressable id" is an ordinary state the tray renders, not an exception.
 *   2. ITS RULE IS LOOSER. It splits on `-` OR `.` and returns the LAST 24-character token,
 *      whatever those characters are. A user file called `holidayphotos2026inparis.jpg` has a
 *      24-character base name and would be handed back as an uploaded-file id, which the edge
 *      answers 404 for on every rendition kind — silently, and for the whole session.
 *      It has ZERO callers today (grep, 2026-09-17), so tightening it in place would be a change
 *      with no test and no beneficiary.
 *
 * WHAT THIS PORTS INSTEAD. The shared library's `FileNameGenerator.getUploadedFileIdFromXConvertFile`
 * (`conversions/FileNameGenerator.kt:140-148`) over the names `createInputFileName` writes
 * (`:75-81`):
 *
 *     <random>-x-<uploadedFileIdHex>.<ext>          a plain upload
 *     <random>-x-<uploadedFileIdHex>-<page>.<ext>   a paged output
 *
 * so: split on `-x-`, take the LAST part, drop the extension, and if what remains contains `-`
 * take segment 0. A 24-hex check is added on top — the one divergence from the Kotlin, in the safe
 * direction, because the Kotlin's caller already knows it holds an xconvert name and this one does
 * not. Anything that does not match is `null`, and a clip with a null id simply never asks the edge
 * for a rendition -- it previews from the local file and, after a reload, from nothing, which is
 * the honest outcome for a name we cannot address.
 *
 * This module takes no container edge: `ApiConfig.suffixDomainWithHttpsEdgeEndpoint` is a static
 * method (`services/api-config.ts:142-144`) and everything else is a model or a type.
 *
 * WHY `ApiConfig` AND NOT `media/urls.ts`'s `edgeBaseFor`, whose spec calls itself "the only place
 * a base should be composed" (`media/urls.spec.ts:31`). Two composers now coexist and they are not
 * interchangeable. `edgeBaseFor` appends `/v2/edge` to a string and nothing else -- it cannot add
 * a scheme, and it does not know about the dev proxy. What arrives here is
 * `ProcessorMetadata.uploadDomain`, a BARE HOST (`qa-1.xconvert.com`), so it needs
 * `https://` in front of it and needs to become a same-origin path under `npm run dev:qa`
 * (`services/dev-proxy.ts`). `ApiConfig.originForDomain` is the shipped answer to exactly that
 * question and is what every other edge URL on the site is built with. The two cannot disagree
 * downstream because `edgeBaseFor` is idempotent: it returns a base that already ends in
 * `/v2/edge` unchanged, so a `StoredClipRef.uploadDomain` written by `storedClipRefOf` (already
 * resolved) survives a later pass through the media layer untouched. Worth one line in P6.2's docs
 * sync: `edgeBaseFor` composes a base from a base, `ApiConfig` resolves an ORIGIN from a host.
 */
import { ApiConfig } from '../../../services/api-config';
import type { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import type { ClipRuntime } from '../views/view-props';
import type { ClipSource, MediaKind } from '../media/types';
import type { StoredClipRef } from './persistence';
import { STUDIO_IMAGE_EXTENSIONS } from '../studio-gate';

/**
 * Every video container xconvert converts — round 8 (O3, S2): `ConvertFromVideo.EXTENTIONS`
 * (`types/capability.types.ts`), no longer §6.8's four. The browser plays only MP4/M4V/WebM of these
 * (`media/playable.ts`); the rest are ADMITTED and previewed from the edge's 540p `preview`
 * rendition, which `RenditionPlan.forUpload` plans for every `CATEGORY_VIDEO` upload (the same
 * ffmpeg the cutter's COMPRESS job uses, without a /downloads card) — see `state/preview-conversion.ts`.
 * Kept as a literal list, with a spec pinning it to the capability class, so this leaf imports
 * nothing from the page's type graph.
 */
export const STUDIO_VIDEO_EXTENSIONS: ReadonlySet<string> = new Set([
    'mts', '3gp', 'avi', 'flv', 'm4v', 'mkv', 'mp4', 'mpeg', 'mpg', 'vob', 'webm', 'wmv', 'mov',
]);

/**
 * Every audio container xconvert converts — `ConvertFromAudio.EXTENTIONS`. MP3/M4A/WAV play in the
 * element; the rest take the edge's 96 kbps M4A `preview` (`RenditionPlan`: `CATEGORY_AUDIO`).
 */
export const STUDIO_AUDIO_EXTENSIONS: ReadonlySet<string> = new Set([
    'mp3', 'aac', 'ogg', 'flac', 'aiff', 'mmf', 'm4a', 'wma', 'wav', 'm4r', 'opus',
]);

/**
 * Everything the Studio's uploader accepts, as `UploadPolicy.acceptedExtensions`.
 *
 * A SUPERSET of the page's own list on purpose (controller ruling 6): `/convert-jpg-to-video`
 * accepts one extension and the Studio on that page still takes a PNG, a clip and a song, because
 * the thing being built is one video and not one conversion of one JPG. Endpoint resolution still
 * rides the page's `fromType`/`toType` — the wider list changes what the VALIDATOR accepts and
 * nothing else.
 */
export const STUDIO_ACCEPTED_EXTENSIONS: string[] = [
    ...STUDIO_IMAGE_EXTENSIONS, ...STUDIO_VIDEO_EXTENSIONS, ...STUDIO_AUDIO_EXTENSIONS,
].sort();

/**
 * The same list as an `<input type="file" accept>` value (S-17): `.jpg,.png,…`. The picker's own
 * filter, so the OS dialog greys out what the uploader would reject; the uploader's policy is
 * still the validator, and a file dragged past the filter is still refused by it.
 */
export const STUDIO_ACCEPT_TYPES: string = STUDIO_ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');

/** The shared library's name rule, ported. Returns null when the name is not one of ours. */
export function uploadedFileIdOf(location: string | null | undefined): string | null {
    if (!location) return null;
    const marker = location.lastIndexOf('-x-');
    if (marker < 0) return null;
    const tail = location.slice(marker + 3);
    const dot = tail.lastIndexOf('.');
    const base = dot > 0 ? tail.slice(0, dot) : tail;
    const id = base.includes('-') ? base.split('-')[0] : base;
    return /^[0-9a-f]{24}$/i.test(id) ? id : null;
}

/** Which of the three the Studio treats this file as. Null means "not something we take". */
export function kindOfExtension(ext: string): MediaKind | null {
    const e = (ext || '').toLowerCase();
    if (STUDIO_IMAGE_EXTENSIONS.has(e)) return 'image';
    if (STUDIO_VIDEO_EXTENSIONS.has(e)) return 'video';
    if (STUDIO_AUDIO_EXTENSIONS.has(e)) return 'audio';
    return null;
}

/** The extension of a file NAME, lower-cased and without the dot. `''` when there is none. */
export function extensionOf(name: string): string {
    const i = (name || '').lastIndexOf('.');
    return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

/**
 * A finished upload as a `ClipSource`, or null when the edge has not answered yet.
 *
 * `localFile` is kept only while the tab lives (§5.0 and P5a's own note on the field): after a
 * reload the record has no bytes and the server rendition takes over, which is the whole reason
 * the Studio can promise a preview at all without rehydrating megabytes from IndexedDB (D-9).
 *
 * The extension comes from the STORED name first and the original name second. They agree for
 * every upload this tier makes (`createInputFileName` keeps the extension verbatim), and when they
 * cannot the stored one is the one the edge will serve renditions under.
 */
export function clipSourceOf(
    upload: UploadInProgressFile<any>,
    clipId: string,
): ClipSource | null {
    const response = upload?.getResponse?.();
    const metadata = upload?.getMetadata?.();
    const processor = metadata?.processorMetadataList?.[0];
    if (!response || !processor?.uploadDomain) return null;
    const uploadedFileId = uploadedFileIdOf(response.location);
    if (!uploadedFileId) return null;
    const raw = upload.file as any;
    const name = response.originalName || raw?.name || '';
    const ext = extensionOf(response.location) || extensionOf(name);
    const kind = kindOfExtension(ext);
    if (!kind) return null;
    return {
        clipId,
        uploadedFileId,
        safeName: response.location,
        token: response.token || '',
        edgeBase: clipEdgeBaseOf(processor.uploadDomain),
        name,
        size: Number(raw?.size ?? processor.fileMetadata?.size ?? 0) || 0,
        ext,
        kind,
        localFile: raw instanceof Blob ? raw : null,
    };
}

/** The persisted half: everything except the bytes (§6.4, "never the `File`"). */
export function storedClipRefOf(src: ClipSource): StoredClipRef {
    return {
        clipId: src.clipId,
        uploadedFileId: src.uploadedFileId,
        safeName: src.safeName,
        token: src.token,
        uploadDomain: src.edgeBase,
        name: src.name,
        size: src.size,
        ext: src.ext,
        kind: src.kind as StoredClipRef['kind'],
    };
}

/**
 * R4-JE-02 (review 7): THE one builder of a clip's edge base -- `<origin>/v2/edge`, every rendition, probe and download URL
 * hangs off it. Given whatever a caller holds: a bare host (`ProcessorMetadata.uploadDomain`, a fresh upload), a base that
 * is already resolved (`StoredClipRef.uploadDomain` as `storedClipRefOf` writes it -- returned unchanged, never doubled), or a
 * bare ORIGIN (`https://host`, what the /downloads rebuild located its inputs on -- the host is taken and resolved like a
 * fresh one). The rebuilt project used to copy that origin straight into `edgeBase`: every rendition went to
 * `https://host/uploads/<id>/renditions/*` without the context path and failed, so it never showed a picture.
 */
export function clipEdgeBaseOf(domain: string | null | undefined): string {
    const raw = String(domain ?? '').trim().replace(/\/+$/, '');
    if (!raw) return '';
    if (raw.endsWith(ApiConfig.edgePathPrefix)) return raw;
    let host = raw;
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
        try { host = new URL(raw).host || raw; } catch { host = raw; }
    }
    return ApiConfig.suffixDomainWithHttpsEdgeEndpoint(host);
}

/**
 * A stored reference back to a source after a reload. No bytes — that is the point.
 *
 * `uploadDomain` on a record `storedClipRefOf` wrote is already the RESOLVED `edgeBase`; `clipEdgeBaseOf` returns such a
 * base unchanged (never a doubled `/v2/edge`) and resolves anything else -- a rebuilt record's bare origin -- exactly as a
 * fresh upload's host is resolved (R4-JE-02).
 */
export function clipSourceOfStored(ref: StoredClipRef): ClipSource {
    return {
        clipId: ref.clipId,
        uploadedFileId: ref.uploadedFileId,
        safeName: ref.safeName,
        token: ref.token,
        edgeBase: clipEdgeBaseOf(ref.uploadDomain),
        name: ref.name,
        size: ref.size,
        ext: ref.ext,
        kind: ref.kind,
        localFile: null,
    };
}

/**
 * The tray's view of one staged file, before any rendition has been asked for.
 *
 * WHAT IT DELIBERATELY DOES NOT KNOW. `natural`, `durationMs`, `posterUrl` and `animated` come
 * back `null`/`false` every time. `natural` in particular must never come from a tile's RETAINED
 * size (P5b.7 §4 rule 8, P5b.6 correction (j)) — a `thumb` decodes to at most 160 px and §6.8's
 * frame default would become a 160x120 video. Its two witnesses (R14-S12, rule 8 as re-worded) are
 * the decode's own `natural` — the file's full ORIENTED size, read before the target resize — and
 * `input/info`, reconciled by `model/frame.ts` `reconcileNatural`. A caller that has learned any
 * of the four keeps its own value: this function builds the UPLOAD half of the runtime, and a
 * consumer that replaces a whole `ClipRuntime` with a fresh one from here will throw the probe's
 * answers away.
 *
 * `upload` with no record at all is `ready`: that is the restored case, where the bytes were
 * uploaded in a previous session and there is nothing in flight to report.
 */
export function runtimeOf(
    upload: UploadInProgressFile<any> | null,
    src: ClipSource | null,
    clipId: string | null,
): ClipRuntime | null {
    if (!src) return null;
    const failed = upload?.error === true;
    const complete = upload ? upload.getComplete?.() === true : true;
    return {
        clipId,
        source: src,
        upload: failed ? 'failed' : complete ? 'ready' : 'uploading',
        uploadPercent: upload ? Number(upload.getProgress?.() ?? 0) : 100,
        posterUrl: null,
        rendition: 'pending',
        natural: null,
        durationMs: null,
        hasAudio: src.kind !== 'image',
        animated: false,
    };
}
